import { useState } from 'react';
import { ChevronDown, ChevronUp, KeyRound, RefreshCw } from 'lucide-react';

import {
  KubesoloHostResult,
  KubesoloHostState,
  KubesoloStepState,
  KubesoloTest,
} from '@api/types.gen';

import { Icon } from '@@/Icon';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { LoadingButton } from '@@/buttons';
import { confirm } from '@@/modals/confirm';
import { buildConfirmButton } from '@@/modals/utils';
import { ModalType } from '@@/modals';

import { useHostOutput } from '../queries/useHostOutput';
import { useReplaceKeyMutation } from '../queries/useReplaceKeyMutation';
import { useRetestMutation } from '../queries/useRetestMutation';
import { formatHost } from '../parseMachines';

import { FailureMessage } from './FailureMessage';
import { ExpandableText } from './ExpandableText';
import { StepList, hasListedSteps } from './StepList';
import { formatDuration, hostDurationMs } from './durations';
import { explainFailure } from './stepFailures';
import {
  runningStepLabels,
  stepLabels,
  stepOrder,
  stepStateIcons,
} from './steps';
import { useNow } from './useNow';

const LIVE_OUTPUT_LINES = 3;

const hostStateIcons: Record<KubesoloHostState, KubesoloStepState> = {
  failed: 'failed',
  running: 'running',
  queued: 'pending',
  passed: 'passed',
  alreadyInstalled: 'passed',
};

interface Props {
  host: KubesoloHostResult;
  test: KubesoloTest;
  hideFix?: boolean;
}

/** One machine on a line: what it's doing or why it failed, with its steps on hover. Expanded, its fix and output. */
export function HostItem({ host, test, hideFix }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const retestMutation = useRetestMutation();
  const replaceKeyMutation = useReplaceKeyMutation();
  const now = useNow(host.state === 'running');

  const isQueued = host.state === 'queued';
  const failedSteps = host.steps.filter((step) => step.state === 'failed');
  const { icon, mode, spin } = stepStateIcons[hostStateIcons[host.state]];

  return (
    <li className="border-x-0 py-2">
      <button
        type="button"
        className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-x-2 border-0 bg-transparent p-0 text-left"
        onClick={() => setIsExpanded(!isExpanded)}
        disabled={isQueued}
        aria-expanded={isQueued ? undefined : isExpanded}
        data-cy="kubesolo-host-toggle"
      >
        <Icon icon={icon} mode={mode} spin={spin} />
        <span className="break-words">
          <span className="font-medium">{formatHost(host)}</span>
          {host.hostname && (
            <span className="text-muted small ml-2">{host.hostname}</span>
          )}
        </span>
        {!isQueued && (
          <>
            <span className="text-muted small whitespace-nowrap">
              {formatDuration(hostDurationMs(host, now))}
            </span>
            <Icon icon={isExpanded ? ChevronUp : ChevronDown} />
          </>
        )}
        {!(isExpanded && failedSteps.length > 0) && (
          <span className="col-span-3 col-start-2">
            <Summary host={host} test={test} now={now} />
          </span>
        )}
      </button>

      {isExpanded && (
        <div className="mt-2 flex flex-col gap-2 pl-6">
          {failedSteps.map((step) => (
            <FailureMessage
              key={step.name}
              step={step}
              test={test}
              hideFix={hideFix}
            />
          ))}

          {failedSteps.length > 0 && (
            <div className="flex gap-2">
              {host.hostKeyChange && (
                <LoadingButton
                  size="small"
                  color="primary"
                  icon={KeyRound}
                  isLoading={replaceKeyMutation.isLoading}
                  loadingText="Replacing key..."
                  onClick={handleReplaceKey}
                  data-cy="kubesolo-replace-key-button"
                >
                  Replace key
                </LoadingButton>
              )}
              <LoadingButton
                size="small"
                color="default"
                icon={RefreshCw}
                isLoading={retestMutation.isLoading}
                loadingText="Starting..."
                onClick={() =>
                  retestMutation.mutate({
                    testId: test.id,
                    hostIds: [host.id],
                  })
                }
                data-cy="kubesolo-retest-host-button"
              >
                Re-test
              </LoadingButton>
            </div>
          )}

          {host.outputTail && <HostOutput host={host} test={test} />}
        </div>
      )}
    </li>
  );

  async function handleReplaceKey() {
    const change = host.hostKeyChange;
    if (!change) {
      return;
    }

    const confirmed = await confirm({
      title: 'Replace host key',
      modalType: ModalType.Warn,
      message: (
        <>
          <p>
            {formatHost(host)} now presents <code>{change.presented}</code>,
            instead of the pinned <code>{change.pinned}</code>.
          </p>
          <p>
            A reinstalled machine gets a new key, and so does one pretending to
            be it. Replace the key only if you know why it changed.
          </p>
        </>
      ),
      confirmButton: buildConfirmButton('Replace key', 'warning'),
    });

    if (!confirmed) {
      return;
    }

    replaceKeyMutation.mutate({
      testId: test.id,
      hostId: host.id,
      fingerprint: change.presented,
    });
  }
}

/** The machine's full kubesoloctl output, fetched once it is shown. */
function HostOutput({
  host,
  test,
}: {
  host: KubesoloHostResult;
  test: KubesoloTest;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const outputQuery = useHostOutput(test.id, host, { enabled: isOpen });

  return (
    <ExpandableText
      label="kubesoloctl output"
      text={outputQuery.data ?? ''}
      isLoading={outputQuery.isLoading}
      onOpenChange={setIsOpen}
      copyAriaLabel={`Copy the output of ${host.host}`}
      data-cy="kubesolo-output"
    />
  );
}

/** The machine's one-line status, which lists its steps on hover. */
function Summary({
  host,
  test,
  now,
}: {
  host: KubesoloHostResult;
  test: KubesoloTest;
  now: number;
}) {
  const text = (
    <span className="text-muted break-words">{summary(host, test)}</span>
  );

  if (!hasListedSteps(host)) {
    return text;
  }

  return (
    <TooltipWithChildren message={<StepList host={host} now={now} />}>
      {text}
    </TooltipWithChildren>
  );
}

/** The one line a machine shows. */
function summary(host: KubesoloHostResult, test: KubesoloTest) {
  switch (host.state) {
    case 'failed': {
      const step = host.steps.find(({ state }) => state === 'failed');
      return (
        step && (
          <>
            {stepLabels[step.name]}: {explainFailure(step, test).message}
          </>
        )
      );
    }
    case 'running': {
      const step = host.steps.find(({ state }) => state === 'running');
      if (!step) {
        return null;
      }
      const label = `Step ${stepOrder.indexOf(step.name) + 1}/${stepOrder.length} - ${runningStepLabels[step.name]}`;
      if (step.progress !== undefined) {
        return `${label} ${step.progress}%`;
      }
      const output = lastLines(host.outputTail);
      return output ? `${label}: ${output}` : label;
    }
    case 'queued':
      return host.queuePosition === 1
        ? 'Next, waiting for a free worker'
        : 'Waiting for a free worker';
    case 'alreadyInstalled':
      return 'KubeSolo is already installed';
    case 'passed':
      return 'All checks passed';
  }
}

/** The last lines kubesoloctl printed, which name the checks as they pass. */
function lastLines(output = '') {
  return output
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(-LIVE_OUTPUT_LINES)
    .join(' / ');
}
